import type { DocumentRenameDialogContentFragment } from "../client/generated";
import { toast } from "sonner";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Input } from "@acdc2/ui/components/input";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { Button } from "@acdc2/ui/components/button";
import { useRenameDocumentMutation } from "../client/generated";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@acdc2/ui/components/dialog";

type Props = {
  fragment: DocumentRenameDialogContentFragment;
  onDone?: () => void;
};

export function DocumentRenameDialogContent({ fragment, onDone }: Props) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState(fragment.name);
  const [renameDocument, { loading }] = useRenameDocumentMutation();

  const onSubmit = async () => {
    try {
      await renameDocument({
        variables: { documentId: fragment.id, newName },
      });
      onDone?.();
    } catch (error) {
      toast(t("DocumentRenameDialogContent.error"), {
        description: new String(error),
      });
    }
  };

  return (
    <DialogContent showCloseButton={false} data-testid="document-rename-dialog">
      <DialogHeader>
        <DialogTitle>{t("DocumentRenameDialogContent.title")}</DialogTitle>
        <DialogDescription>
          <Trans
            i18nKey="DocumentRenameDialogContent.description"
            components={{ "word-break": <span className="break-all" /> }}
            values={{ name: fragment.name }}
          />
        </DialogDescription>
      </DialogHeader>
      <Input
        placeholder={t("DocumentRenameDialogContent.placeholder")}
        value={newName}
        onChange={(event) => setNewName(event.target.value)}
        data-testid="document-rename-input"
      />
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="secondary">
            {t("DocumentRenameDialogContent.cancel")}
          </Button>
        </DialogClose>

        <AccessButton
          loading={loading}
          disabled={newName.length === 0 || loading}
          onClick={onSubmit}
          children={t("DocumentRenameDialogContent.submit")}
          data-testid="document-rename-submit-button"
        />
      </DialogFooter>
    </DialogContent>
  );
}
